import React, { useState, useEffect, useCallback } from "react";
import {
  AddPaymentButton,
  BillingPrice,
  CardContainer,
  CardHeading,
  CardHeadingContainer,
  CardInfo,
  CardSubContainer,
  ChangePlanText,
  Duration,
  EditButton,
  FilterContainer,
  InternalHeading,
  LoadingContainer,
  NoCardContainer,
  PlanInfoContainer,
  PlanName,
  SingleCard,
  SubContainer,
  TableContainer,
  TableHeadingContainer,
  SecurePaymentText,
  PlanHeadingContainer,
} from "./styles";
import TextView from "../../TextView";
import Button from "../../Button";
import { Box, CircularProgress } from "@mui/material";
import Table from "../../Table";
import {
  cardIcon,
  cardNames,
  durationDisplay,
  durationOptions,
  paymentTableColumns,
} from "./helper";
import {
  getAllTransactions,
  getAllCards,
  getCurrentPlan,
  downloadInvoice,
} from "../../../services/api";
import moment from "moment";
import PaymentMethods from "../../PaymentMethods";
import Dropdown from "../../Dropdown";
import { filterOptions } from "./helper";
import DateRangePicker from "../../DateRange";
import { useSelector } from "react-redux";
import CustomPagination from "../../Pagination";

export const BillingForm = ({ openSubscriptionTab }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [results, setResults] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [paymentPopup, setPaymentPopup] = useState(false);
  const [defaultCard, setDefaultCard] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isCalendarVisible, setCalendarVisible] = useState(false);
  const [filter, setFilter] = useState("all");
  const [forcePopupClose, setForcePopupClose] = useState(false);
  const [loadOnlyTable, setLoadOnlyTable] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);

  const { userProfile } = useSelector((state) => state.userProfile);

  const fetchData = useCallback(async () => {
    try {
      const formattedStartDate = startDate
        ? moment(startDate).format("YYYY-MM-DD")
        : null;
      const formattedEndDate = endDate
        ? moment(endDate).format("YYYY-MM-DD")
        : null;

      if (filter !== "date" && page === 1) {
        setIsLoading(true);
      }

      const offset = (page - 1) * 10;

      const [transactionResponse, currentPlanResponse] =
        await Promise.allSettled([
          getAllTransactions(formattedStartDate, formattedEndDate, 10, offset),
          getCurrentPlan(),
        ]);

      setResults(transactionResponse.value?.data?.results || []);
      setCurrentPlan(currentPlanResponse.value?.data?.data);
      setTotalRecords(transactionResponse.value?.data?.count);
      setTimeout(() => {
        setIsLoading(false);
        setLoadOnlyTable(false);
      }, 1000);
    } catch (err) {
      setIsLoading(false);
      console.log(err);
    }
  }, [startDate, endDate, filter, page]);

  const fetchCards = useCallback(async () => {
    const response = await getAllCards();
    if (response.data?.cards?.length === 0) {
      setForcePopupClose(true);
    } else {
      setForcePopupClose(false);
    }
    const card = response?.data?.cards?.find(
      (card) => card.is_default || response?.data?.cards?.[0]
    );
    if (card) {
      if (card.id !== defaultCard?.id) {
        setDefaultCard(card);
      }
    } else {
      // No cards found, clear the defaultCard state
      setDefaultCard(null);
    }
  }, [defaultCard?.id]);

  useEffect(() => {
    fetchCards();
  }, []);

  useEffect(() => {
    if (
      filter === "all" ||
      (filter === "date" && startDate && endDate) ||
      page > 1
    ) {
      setLoadOnlyTable(true);
      fetchData();
    }
  }, [startDate, endDate, filter, page]);

  useEffect(() => {
    if (
      results?.length > 0 &&
      (selectedRows.length !== results.length ||
        !results.every((r, i) => r.id === selectedRows[i]?.id))
    ) {
      setSelectedRows(results);
    }
  }, [results, selectedRows]);

  const closePopup = () => {
    setPaymentPopup(false);
    fetchCards();
  };

  const handleCardChanged = () => {
    fetchCards();
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const response = await downloadInvoice({
        transaction_ids: selectedRows.map((row) => row.id),
      });

      const invoiceUrl = response.data.data.data.invoice_url;

      fetch(invoiceUrl)
        .then((response) => response.blob())
        .then((blob) => {
          const blobUrl = window.URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = blobUrl;
          link.setAttribute("download", "Invoice.pdf");
          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
          setDownloading(false);
        })
        .catch((error) => {
          console.error("Download failed:", error);
          setDownloading(false);
        });
    } catch (err) {
      setDownloading(false);
      console.log("error", err);
    }
  };

  const addNewPayment = () => {
    setPaymentPopup(true);
  };

  const handleFilterChange = (e) => {
    const selectedFilter = e.target.value;
    setFilter(selectedFilter);

    if (selectedFilter !== "date") {
      setStartDate(null);
      setEndDate(null);
      setCalendarVisible(false);
    } else {
      setCalendarVisible(true);
    }
  };

  const handleForceFilterChange = () => {
    setFilter("date");
    setStartDate(null);
    setEndDate(null);
    setCalendarVisible(true);
  };

  const formattedDateRange =
    startDate && endDate
      ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
      : null;

  const handleDateSelection = () => {
    if (startDate && endDate) {
      setCalendarVisible(false);
    }
  };

  const handlePaginationChange = (newOffset) => {
    setPage(newOffset / 10 + 1);
  };

  const handleRowSelection = useCallback((selectedRows) => {
    setSelectedRows(selectedRows);
  }, []);

  return isLoading ? (
    <LoadingContainer>
      <CircularProgress />
    </LoadingContainer>
  ) : (
    <div>
      <CardContainer>
        <SingleCard>
          {defaultCard && (
            <CardHeadingContainer>
              <CardHeading>Current Plan</CardHeading>
              {currentPlan?.sub_tier !== 0 && (
                <ChangePlanText onClick={openSubscriptionTab}>
                  Change Plan
                </ChangePlanText>
              )}
            </CardHeadingContainer>
          )}
          <PlanInfoContainer hasCard={!!defaultCard}>
            <PlanHeadingContainer>
              {!defaultCard && <TextView>Current Plan</TextView>}
              <PlanName hasDueDate={currentPlan?.valid_upto ? true : false}>
                <InternalHeading>
                  {currentPlan?.name || "Basic"}
                </InternalHeading>
                <Duration>
                  <TextView variant="tag" color={"#29C18C"}>
                    {durationDisplay[currentPlan?.price?.duration] || "Free"}
                  </TextView>
                </Duration>
              </PlanName>
            </PlanHeadingContainer>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                height: "100%",
              }}
            >
              <div>
                {currentPlan?.valid_upto ? (
                  <TextView
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <span>Due on</span>
                    <span style={{ fontWeight: 700 }}>
                      {moment(currentPlan?.valid_upto).format("Do MMM YYYY")}
                    </span>
                  </TextView>
                ) : (
                  <TextView>
                    <span>Member since </span>
                    <span style={{ fontWeight: 700 }}>
                      {moment(userProfile?.created_at).format("MMMM DD, YYYY")}
                    </span>
                  </TextView>
                )}
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <BillingPrice>
                    $
                    {currentPlan?.price?.price
                      ? `${currentPlan.price.price}/`
                      : "0/"}
                  </BillingPrice>
                  <TextView>
                    {durationOptions[currentPlan?.price?.duration] || "Month"}
                  </TextView>
                </div>
              </div>
              {currentPlan?.sub_tier === 0 && (
                <ChangePlanText onClick={openSubscriptionTab}>
                  Upgrade Subscription
                </ChangePlanText>
              )}
            </div>
          </PlanInfoContainer>
        </SingleCard>
        <SingleCard>
          {defaultCard && (
            <CardSubContainer>
              <CardHeading>Payment Method</CardHeading>
              <EditButton onClick={() => setPaymentPopup(true)}>
                Edit
              </EditButton>
            </CardSubContainer>
          )}
          {defaultCard ? (
            <PlanInfoContainer hasCard={!!defaultCard}>
              <CardInfo>
                <img
                  src={cardIcon[defaultCard.brand]}
                  alt="visa"
                  width={44}
                  height={30}
                />
                <div>
                  <TextView color={"#565556"}>
                    {cardNames[defaultCard.brand]} ending with{" "}
                    {defaultCard.last4}
                  </TextView>
                  <TextView color={"#939393"}>
                    Expiry {defaultCard.expiry_month}/{defaultCard.expiry_year}
                  </TextView>
                </div>
              </CardInfo>
            </PlanInfoContainer>
          ) : (
            <NoCardContainer>
              <SubContainer>
                <InternalHeading>Set Up Your Payments</InternalHeading>
                <SecurePaymentText>
                  Securely save a payment method to make transactions faster and
                  easier.
                </SecurePaymentText>
                <AddPaymentButton>
                  <Button onClick={addNewPayment}>Add Payment Method</Button>
                </AddPaymentButton>
              </SubContainer>
            </NoCardContainer>
          )}
        </SingleCard>
      </CardContainer>

      <TableContainer>
        <TableHeadingContainer>
          <InternalHeading>Billing History</InternalHeading>
          <FilterContainer>
            <Button
              variant={"outlined"}
              width={140}
              disabled={selectedRows.length === 0 || downloading}
              onClick={handleDownload}
            >
              {downloading ? (
                <CircularProgress
                  size={16}
                  color="inherit"
                  style={{ marginRight: "5px" }}
                />
              ) : null}
              Download Invoice
            </Button>
            <Dropdown
              options={filterOptions}
              value={filter}
              onChange={handleFilterChange}
              style={{ width: filter === "date" ? 170 : 140, marginTop: "0px" }}
              variant="outlined"
              handleForceChange={handleForceFilterChange}
              renderValue={(selected) =>
                selected === "date" && startDate && endDate
                  ? formattedDateRange
                  : filterOptions.find((option) => option.value === selected)
                      ?.label
              }
            />
            {filter === "date" && isCalendarVisible && (
              <DateRangePicker
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                onComplete={handleDateSelection}
              />
            )}
          </FilterContainer>
        </TableHeadingContainer>

        {loadOnlyTable ? (
          <LoadingContainer>
            <CircularProgress />
          </LoadingContainer>
        ) : (
          <Box mt={4} mb={5}>
            <Table
              columns={paymentTableColumns}
              data={results}
              isSelectable={true}
              onSelectRows={handleRowSelection}
              selectedRows={selectedRows}
              noPadding={true}
              paginationTotalRows={totalRecords}
              currentPage={page}
              rowsPerPage={10}
            />
            {totalRecords > 0 && (
              <CustomPagination
                currentPage={page}
                onPaginationClicked={handlePaginationChange}
                totalRecords={totalRecords}
              />
            )}
          </Box>
        )}
      </TableContainer>

      {paymentPopup && (
        <PaymentMethods
          open={paymentPopup}
          handleClose={closePopup}
          forcePopupClose={forcePopupClose}
          onCardChanged={handleCardChanged}
        />
      )}
    </div>
  );
};
